import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { TemporaryNumber } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';
import { parsePhoneNumber } from '../../utils/numberFormatter';

interface TemporaryNumberCardProps {
  temporaryNumber: TemporaryNumber;
}

export function TemporaryNumberCard({ temporaryNumber }: TemporaryNumberCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const isVoice = temporaryNumber.serverId === 5;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-500';
      case 'resendable':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'waiting':
        return 'bg-amber-500/20 text-amber-400';
      case 'expired':
        return 'bg-neutral-dark text-neutral-medium';
      case 'refunded':
        return 'bg-blue-500/20 text-blue-500';
      case 'cancelled':
        return 'bg-red-500/20 text-red-500';
      default:
        return 'bg-neutral-dark text-neutral-medium';
    }
  };

  const getStatusLabel = (status: string): string => {
    return t(`temporaryNumbers.status.${status}`);
  };

  const cardContent = (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-heading font-semibold text-brand-accent mb-1">
            {(() => {
              const { countryCode, localNumber } = parsePhoneNumber(temporaryNumber.number);
              return `${countryCode} ${localNumber}`;
            })()}
          </h3>
          <p className="text-sm text-neutral-medium font-body">
            {temporaryNumber.serviceName}
          </p>
        </div>
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-body ${getStatusColor(temporaryNumber.status)}`}>
          {getStatusLabel(temporaryNumber.status)}
        </span>
      </div>

      {/* Details */}
      <div className="flex items-center justify-between text-sm">
        <div>
          {!isVoice && (
            <p className="text-neutral-medium font-body">
              {t('temporaryNumbers.details.country')}:{' '}
              <span className="text-neutral-light">{temporaryNumber.countryName}</span>
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-neutral-medium font-body">
            {t('temporaryNumbers.details.price')}
          </p>
          <p className="text-lg font-heading font-bold text-brand-accent">
            {(temporaryNumber.price ?? 0).toFixed(2)} USD
          </p>
        </div>
      </div>

      {/* Resend info */}
      {!isVoice && temporaryNumber.resendExpiresAt && new Date(temporaryNumber.resendExpiresAt) > new Date() && (
        <div className="flex items-center justify-between text-xs text-neutral-medium font-body pt-2">
          <span>
            {t('temporaryNumbers.details.resendAvailableUntil')}:{' '}
            <span className="text-emerald-400 font-semibold">
              {formatDate(temporaryNumber.resendExpiresAt, 'PP h:mm a', currentLanguage)}
            </span>
          </span>
          {temporaryNumber.resendPrice != null && temporaryNumber.resendPrice > 0 && (
            <span className="text-brand-accent font-semibold">
              {Number(temporaryNumber.resendPrice).toFixed(2)} USD
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="pt-3 border-t border-neutral-dark flex items-center justify-between">
        <div className="text-xs text-neutral-medium font-body">
          {isVoice ? t('voice.receiverTitle') : t('temporaryNumbers.details.expiresAt')}
          {!isVoice && ': '}
          {!isVoice && formatDate(temporaryNumber.expiresAt, 'PP h:mm a', currentLanguage)}
        </div>
        {isVoice && temporaryNumber.status === 'active' && (
          <a
            href={`/call/${temporaryNumber._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1 bg-brand-accent text-brand-primary rounded-lg font-heading font-bold hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300"
          >
            📞 {t('voice.openReceiver')}
          </a>
        )}
      </div>
    </div>
  );

  if (isVoice) {
    return (
      <div className="block bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg p-4 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group">
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      to={`/temporary-numbers/${temporaryNumber._id}`}
      className="block bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg p-4 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
    >
      {cardContent}
    </Link>
  );
}

